// V-24 語音設定與聽不懂統計（網頁；docs/panel/09-voice-calendar.md §2.7、§2.8、§8、§9.8；05-ui-ux V-24；VC2）。只限主人。
//   - 開關、喚醒詞（存起來，VC3 才生效）、語言（只有國語；其他灰色「之後」）、聽不懂時用網路幫忙（VC4 才生效，先存）、
//     讓我看聽不懂的句子（share_miss_text：打開時說明面板上會告知長輩；關掉 = 雲端立刻刪掉已上傳的句子）。
//   - 教它：說法 + 動作（+ 參數）存成這台的本機說法（voice.phrases，整組取代）；422 details.reason 對應文案。
//   - 聽不懂統計：GET voice/misses?days=7「這週有 12 次聽不懂（行事曆 5、打電話 3、其他 4）」；有原文時可展開，每筆［教它］。
//   - 第一次打開的說明（§2.8）：喚醒詞常開時麥克風圖示一直亮、聲音不離開平板、只會台語的長輩第一版還不能用、門鈴 / 對講畫面不能用說的。
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPanelSettings } from './panels';
import './panelCalendar.css';
import {
  MISS_CATEGORIES, TEACH_ACTIONS, VOICE_LANGS, VOICE_PHRASE_MAX_LEN, cleanParams, defaultParams, getVoiceMisses, missBreakdown, parseVoiceSettings,
  phraseError, putVoiceSettings, teachAction, voiceErrKey, voicePatch, wakeWordError,
  type TeachField, type VoiceMisses, type VoicePhrase, type VoiceSettings,
} from './panelVoice';

const actionKey = (a: string) => `panelVoice.action.${a.replace('.', '_')}`;

export function PanelVoiceCard({ panelId }: { panelId: string }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState<VoiceSettings | null>(null);
  const [draft, setDraft] = useState<VoiceSettings | null>(null);
  const [misses, setMisses] = useState<VoiceMisses | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showMissText, setShowMissText] = useState(false);
  const [teach, setTeach] = useState<VoicePhrase>({ text: '', action: 'calendar.query', params: defaultParams('calendar.query') });
  const [teachErr, setTeachErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getPanelSettings(panelId)
      .then((s) => {
        if (!alive) return;
        const v = parseVoiceSettings(s);
        setSaved(v);
        setDraft(v);
      })
      .catch((e) => alive && setLoadErr(voiceErrKey(e)));
    getVoiceMisses(panelId, 7)
      .then((m) => alive && setMisses(m))
      .catch(() => alive && setMisses(null)); // 統計讀不到不擋設定
    return () => {
      alive = false;
    };
  }, [panelId]);

  const set = (p: Partial<VoiceSettings>) => {
    if (!draft) return;
    setDraft({ ...draft, ...p });
    setMsg(null);
    setErr(null);
  };

  const save = async (next: VoiceSettings, okKey: string) => {
    if (!saved) return false;
    const body = voicePatch(saved, next);
    if (!body) {
      setMsg(t('panelVoice.noChanges'));
      return false;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const v = await putVoiceSettings(panelId, body);
      setSaved(v);
      setDraft(v);
      setMsg(t(okKey));
      if (saved.shareMissText && !v.shareMissText) setMisses((m) => (m ? { ...m, items: [] } : m)); // 關掉分享 → 雲端已刪原文
      return true;
    } catch (e) {
      setErr(t(voiceErrKey(e)));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = () => {
    if (!draft || !saved) return;
    if (draft.wakeWord.trim() !== saved.wakeWord) {
      const we = wakeWordError(draft.wakeWord, draft.phrases);
      if (we) {
        setErr(t(`panelVoice.err.${we}`));
        return;
      }
    }
    // 說法另外存（教它），這裡只送設定欄位
    void save({ ...draft, phrases: saved.phrases }, 'panelVoice.saved');
  };

  const addPhrase = async () => {
    if (!saved) return;
    const p: VoicePhrase = { text: teach.text.trim(), action: teach.action, params: cleanParams(teach.action, teach.params) };
    const e = phraseError(p, saved.phrases, saved.wakeWord);
    if (e) {
      setTeachErr(t(`panelVoice.err.${e}`));
      return;
    }
    setTeachErr(null);
    const ok = await save({ ...saved, phrases: [...saved.phrases, p] }, 'panelVoice.teach.added');
    if (ok) setTeach({ text: '', action: teach.action, params: defaultParams(teach.action) });
  };

  const removePhrase = (i: number) => {
    if (!saved) return;
    void save({ ...saved, phrases: saved.phrases.filter((_, j) => j !== i) }, 'panelVoice.teach.removed');
  };

  const teachFrom = (text: string) => {
    setTeach({ ...teach, text: text.slice(0, VOICE_PHRASE_MAX_LEN) });
    setTeachErr(null);
    document.getElementById(`voice-teach-${panelId}`)?.scrollIntoView?.({ block: 'center' });
  };

  const paramLabel = (action: string, params: Record<string, unknown>) => {
    const a = teachAction(action);
    return (a?.fields ?? []).filter((f) => params[f.name] !== undefined).map((f) => {
      const v = params[f.name];
      if (f.kind === 'enum') return t(`panelVoice.opt.${f.name}.${String(v)}`);
      if (f.kind === 'bool') return t(v ? 'panelVoice.opt.on.true' : 'panelVoice.opt.on.false');
      return String(v);
    }).join('、');
  };

  return (
    <div className="card" data-testid="panel-voice">
      <h2>{t('panelVoice.title')}</h2>
      <ul className="text-secondary pv-intro" data-testid="voice-intro">
        <li>{t('panelVoice.intro.mic')}</li>
        <li>{t('panelVoice.intro.local')}</li>
        <li>{t('panelVoice.intro.taiwanese')}</li>
        <li>{t('panelVoice.intro.doorbell')}</li>
      </ul>
      {loadErr && <p className="error-text" role="alert">{t(loadErr)}</p>}
      {draft && saved && (
        <>
          <label className="pc-check">
            <input type="checkbox" role="switch" checked={draft.on} data-testid="voice-on" onChange={(e) => set({ on: e.target.checked })} />
            {t('panelVoice.on')}
          </label>
          {!draft.on && <p className="text-secondary pc-hint">{t('panelVoice.offHint')}</p>}

          <fieldset className="pc-fieldset" disabled={!draft.on}>
            <legend>{t('panelVoice.wakeTitle')}</legend>
            <label className="pc-check">
              <input type="checkbox" role="switch" checked={draft.wakeOn} data-testid="voice-wake-on" onChange={(e) => set({ wakeOn: e.target.checked })} />
              {t('panelVoice.wakeOn')}
            </label>
            <label className="pc-field">
              <span>{t('panelVoice.wakeWord')}</span>
              <input value={draft.wakeWord} maxLength={6} data-testid="voice-wake-word" onChange={(e) => set({ wakeWord: e.target.value })} />
              <small className="text-secondary">{t('panelVoice.wakeWordHint')}</small>
            </label>
            <p className="text-secondary pc-hint" data-testid="voice-wake-later">{t('panelVoice.wakeLater')}</p>
          </fieldset>

          <label className="pc-field">
            <span>{t('panelVoice.langTitle')}</span>
            <select value={draft.lang} data-testid="voice-lang" disabled={!draft.on} onChange={(e) => set({ lang: e.target.value })}>
              {VOICE_LANGS.map((l) => (
                <option key={l.code} value={l.code} disabled={!l.ready}>
                  {t(l.key)}{l.ready ? '' : ` ${t('panelVoice.lang.later')}`}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="pc-fieldset">
            <legend>{t('panelVoice.privacyTitle')}</legend>
            <label className="pc-check">
              <input type="checkbox" role="switch" checked={draft.cloudEnabled} data-testid="voice-cloud" onChange={(e) => set({ cloudEnabled: e.target.checked })} />
              {t('panelVoice.cloud')}
            </label>
            <p className="text-secondary pc-hint">{t('panelVoice.cloudHint')}</p>
            <label className="pc-check">
              <input type="checkbox" role="switch" checked={draft.shareMissText} data-testid="voice-share" onChange={(e) => set({ shareMissText: e.target.checked })} />
              {t('panelVoice.share')}
            </label>
            {draft.shareMissText && !saved.shareMissText && <p className="pc-warn" data-testid="voice-share-notice">{t('panelVoice.shareOnNotice')}</p>}
            {!draft.shareMissText && saved.shareMissText && <p className="pc-warn" data-testid="voice-share-off">{t('panelVoice.shareOffNotice')}</p>}
            {draft.shareMissText && saved.shareMissText && <p className="text-secondary pc-hint">{t('panelVoice.shareOnHint')}</p>}
          </fieldset>

          {err && <p className="error-text" role="alert" data-testid="voice-err">{err}</p>}
          {msg && <p className="text-secondary" role="status">{msg}</p>}
          <div className="dv-pairing-actions">
            <button className="btn-primary" disabled={busy} onClick={saveSettings} data-testid="voice-save">
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </div>

          <h3 className="pc-subhead">{t('panelVoice.missTitle')}</h3>
          {misses === null ? (
            <p className="text-secondary">{t('panelVoice.missUnavailable')}</p>
          ) : misses.total === 0 ? (
            <p className="text-secondary" data-testid="voice-miss-none">{t('panelVoice.missNone')}</p>
          ) : (
            <>
              <p data-testid="voice-miss-summary">
                {t('panelVoice.missSummary', {
                  n: misses.total,
                  parts: missBreakdown(misses).map((b) => t('panelVoice.missPart', { cat: t(`panelVoice.cat.${b.cat}`), n: b.n })).join(t('panelVoice.sep')),
                })}
              </p>
              {misses.items.length > 0 && (
                <>
                  <button className="btn-ghost" onClick={() => setShowMissText(!showMissText)} data-testid="voice-miss-toggle">
                    {showMissText ? t('panelVoice.missHide') : t('panelVoice.missShow', { n: misses.items.length })}
                  </button>
                  {showMissText && (
                    <ul className="pv-miss-list" data-testid="voice-miss-items">
                      {misses.items.map((m, i) => (
                        <li key={`${m.at}-${i}`} className="pv-miss-row">
                          <span>「{m.text}」 <small className="text-secondary">{m.at.slice(5, 10).replace('-', '/')} {m.at.slice(11, 16)} · {t(`panelVoice.cat.${MISS_CATEGORIES.includes(m.category as never) ? m.category : 'other'}`)}</small></span>
                          <button className="btn-ghost" onClick={() => teachFrom(m.text)} data-testid="voice-miss-teach">{t('panelVoice.teach.fromMiss')}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {misses.items.length === 0 && !saved.shareMissText && <p className="text-secondary pc-hint">{t('panelVoice.missNoText')}</p>}
            </>
          )}

          <h3 className="pc-subhead" id={`voice-teach-${panelId}`}>{t('panelVoice.teach.title')}</h3>
          <p className="text-secondary">{t('panelVoice.teach.hint')}</p>
          {saved.phrases.length > 0 && (
            <ul className="pv-phrases" data-testid="voice-phrases">
              {saved.phrases.map((p, i) => (
                <li key={`${p.text}-${i}`} className="pv-phrase-row">
                  <span>「{p.text}」→ {t(actionKey(p.action))}{paramLabel(p.action, p.params) && `（${paramLabel(p.action, p.params)}）`}</span>
                  <button className="btn-ghost" disabled={busy} onClick={() => removePhrase(i)} data-testid="voice-phrase-remove">{t('panelVoice.teach.remove')}</button>
                </li>
              ))}
            </ul>
          )}
          <div className="pc-form" data-testid="voice-teach">
            <label className="pc-field">
              <span>{t('panelVoice.teach.text')}</span>
              <input value={teach.text} maxLength={VOICE_PHRASE_MAX_LEN} placeholder={t('panelVoice.teach.textPh')} data-testid="voice-teach-text"
                onChange={(e) => { setTeach({ ...teach, text: e.target.value }); setTeachErr(null); }} />
            </label>
            <label className="pc-field">
              <span>{t('panelVoice.teach.action')}</span>
              <select value={teach.action} data-testid="voice-teach-action"
                onChange={(e) => { setTeach({ ...teach, action: e.target.value, params: defaultParams(e.target.value) }); setTeachErr(null); }}>
                {TEACH_ACTIONS.map((a) => <option key={a.action} value={a.action}>{t(actionKey(a.action))}</option>)}
              </select>
            </label>
            {(teachAction(teach.action)?.fields ?? []).map((f) => (
              <TeachFieldInput key={f.name} f={f} value={teach.params[f.name]} onChange={(v) => { setTeach({ ...teach, params: { ...teach.params, [f.name]: v } }); setTeachErr(null); }} />
            ))}
            {['sos.start', 'switch.set', 'camera.open'].includes(teach.action) && <p className="text-secondary pc-hint">{t('panelVoice.teach.stillConfirm')}</p>}
            {teachErr && <p className="error-text" role="alert" data-testid="voice-teach-err">{teachErr}</p>}
            <div className="dv-pairing-actions">
              <button className="btn-primary" disabled={busy || !teach.text.trim()} onClick={() => void addPhrase()} data-testid="voice-teach-add">{t('panelVoice.teach.add')}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function TeachFieldInput({ f, value, onChange }: { f: TeachField; value: unknown; onChange: (v: unknown) => void }) {
  const { t } = useTranslation();
  const label = `${t(`panelVoice.field.${f.name}`)}${f.required ? '' : ` ${t('panelVoice.optional')}`}`;
  switch (f.kind) {
    case 'enum':
      return (
        <label className="pc-field">
          <span>{label}</span>
          <select value={value === undefined ? '' : String(value)} data-testid={`voice-teach-p-${f.name}`} onChange={(e) => onChange(e.target.value || undefined)}>
            {!f.required && <option value="">{t('panelVoice.any')}</option>}
            {f.options.map((o) => <option key={o} value={o}>{t(`panelVoice.opt.${f.name}.${o}`)}</option>)}
          </select>
        </label>
      );
    case 'bool':
      return (
        <label className="pc-field">
          <span>{label}</span>
          <select value={value === false ? 'false' : 'true'} data-testid={`voice-teach-p-${f.name}`} onChange={(e) => onChange(e.target.value === 'true')}>
            <option value="true">{t('panelVoice.opt.on.true')}</option>
            <option value="false">{t('panelVoice.opt.on.false')}</option>
          </select>
        </label>
      );
    case 'int':
      return (
        <label className="pc-field">
          <span>{label}</span>
          <input type="number" min={f.min} max={f.max} value={value === undefined ? '' : String(value)} data-testid={`voice-teach-p-${f.name}`}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
        </label>
      );
    case 'text':
      return (
        <label className="pc-field">
          <span>{label}</span>
          <input value={value === undefined ? '' : String(value)} maxLength={f.max} data-testid={`voice-teach-p-${f.name}`} onChange={(e) => onChange(e.target.value)} />
        </label>
      );
  }
}
